import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Server, Database, FolderTree, Globe, Settings, Layers, ShieldCheck } from 'lucide-react';

/*
 * TechnicalDoc.jsx — printable, single-page Architecture & Migration handbook.
 * Click "Download as PDF" → browser print dialog → Save as PDF.
 * Designed for ops/devops to migrate ESD to any cloud server.
 */

const TechnicalDoc = () => {
  const navigate = useNavigate();
  const [branding, setBranding] = useState(null);
  useEffect(() => { (async () => { try { const r = await api.get('/settings/branding'); setBranding(r.data); } catch (e) { /* ignore */ } })(); }, []);
  const today = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="space-y-4">
      <div className="print:hidden flex items-center justify-between border-b pb-3">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="techdoc-back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <Button size="sm" onClick={() => window.print()} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="techdoc-print-btn">
          <Printer className="h-4 w-4 mr-1.5" /> Download as PDF
        </Button>
      </div>

      <article className="bg-white mx-auto max-w-5xl text-slate-900 print:max-w-none" id="techdoc" data-testid="techdoc">
        {/* COVER */}
        <section className="px-10 pt-10 pb-6 border-b-2 border-slate-900 print:break-after-page">
          <div className="flex items-center gap-4">
            {branding?.logo_url ? <img src={branding.logo_url} alt="logo" className="h-14 w-14 object-contain" /> : <div className="h-14 w-14 rounded bg-[hsl(217,91%,24%)] grid place-items-center text-white font-bold text-lg">PIVI</div>}
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">{branding?.header_text || 'PIVI EQUIPMENT SOLUTIONS DEPARTMENT'}</div>
              <h1 className="text-3xl font-bold tracking-tight mt-1">ESD — Technical Architecture &amp; Migration Handbook</h1>
              <div className="text-sm text-slate-600">Frontend ↔ Backend ↔ MongoDB traceability for the Equipment Solutions Department platform</div>
            </div>
          </div>
          <div className="mt-10 max-w-3xl">
            <p className="text-base text-slate-700 leading-relaxed">Everything you need to redeploy ESD on your own infrastructure: configuration files, code paths, every API endpoint mapped to its handler and Mongo collection, deployment topology, and a step‑by‑step migration runbook. Print this document and keep it next to your terminal during cut‑over.</p>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-6 text-sm">
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Generated</div><div className="font-semibold">{today}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">App Version</div><div className="font-semibold">ESD v1.0</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Audience</div><div className="font-semibold">DevOps · Platform Engineers</div></div>
          </div>
        </section>

        {/* 1 — STACK OVERVIEW */}
        <Section title="1 · Stack at a Glance" icon={Server} pageBreak>
          <table className="w-full text-xs border border-slate-200">
            <thead className="bg-slate-50 text-left"><tr><th className="p-2 border-b">Tier</th><th className="p-2 border-b">Tech</th><th className="p-2 border-b">Process</th><th className="p-2 border-b">Listen</th></tr></thead>
            <tbody>
              <Row r={['Frontend', 'React 19 · Tailwind · shadcn/ui · react-router · axios', 'CRA dev server (yarn start) — production: static build behind nginx', 'Port 3000 (dev)']} />
              <Row r={['Backend API', 'FastAPI 0.110 · Motor 3.3 (async Mongo) · PyJWT · bcrypt', 'Uvicorn under supervisord', 'Port 8001']} />
              <Row r={['Database', 'MongoDB 6+', 'External (managed Atlas or self-hosted)', 'mongodb://… via MONGO_URL']} />
              <Row r={['Reverse proxy', 'Kubernetes ingress in current env / nginx in your cloud', 'Routes `/api/*` → :8001, everything else → :3000', '80 / 443']} />
            </tbody>
          </table>
          <ArchSVG />
        </Section>

        {/* 2 — REPO LAYOUT */}
        <Section title="2 · Repository Layout" icon={FolderTree} pageBreak>
          <pre className="text-[11px] leading-relaxed bg-slate-900 text-slate-100 rounded-md p-4 overflow-x-auto whitespace-pre">
{`/app
├── backend
│   ├── server.py          # ~2,750 LOC — all FastAPI routes & business logic
│   ├── models.py          # All Pydantic request/response models + constants
│   ├── auth_utils.py      # JWT encode/decode + bcrypt helpers
│   ├── reset_data.py      # CLI: wipe DB except super.admin
│   ├── requirements.txt   # Python deps (pinned via pip freeze)
│   ├── .env               # MONGO_URL, DB_NAME, CORS_ORIGINS, JWT_SECRET
│   └── tests/             # pytest regression suites
│
├── frontend
│   ├── public/index.html  # SPA shell, tab title
│   ├── src/
│   │   ├── App.js                # React Router, route → page mapping
│   │   ├── api/client.js         # Axios instance + interceptors
│   │   ├── contexts/AuthContext  # JWT in localStorage, role helpers
│   │   ├── components/           # Layout, Combobox, PageHeader, ui/*
│   │   ├── pages/                # One file per route (see §5)
│   │   ├── hooks/, lib/          # use-toast, format, utils
│   │   └── styles.css            # Tailwind base
│   ├── package.json
│   ├── tailwind.config.js · craco.config.js · postcss.config.js
│   └── .env               # REACT_APP_BACKEND_URL  (FRONTEND uses ONLY this for API calls)
│
├── memory                 # PRD.md, CHANGELOG, test_credentials.md (operator handoff)
└── test_reports           # Automated test agent JSON output
`}
          </pre>
        </Section>

        {/* 3 — ENV & CONFIG */}
        <Section title="3 · Configuration &amp; Environment Variables" icon={Settings} pageBreak>
          <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700 mt-1">Backend — /app/backend/.env</h3>
          <table className="w-full text-xs border border-slate-200 mt-2">
            <thead className="bg-slate-50 text-left"><tr><th className="p-2 border-b">Variable</th><th className="p-2 border-b">Used by</th><th className="p-2 border-b">Example</th></tr></thead>
            <tbody>
              <Row r={['MONGO_URL', 'Motor client (server.py line ~57)', 'mongodb+srv://user:pass@host/?retryWrites=true']} />
              <Row r={['DB_NAME', 'Motor client (server.py)', 'esd_production']} />
              <Row r={['CORS_ORIGINS', 'FastAPI CORSMiddleware', 'https://esd.yourcompany.com,https://app.yourcompany.com']} />
              <Row r={['JWT_SECRET', 'auth_utils.py — sign/verify JWTs', 'long-random-string (≥32 chars)']} />
            </tbody>
          </table>

          <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700 mt-4">Frontend — /app/frontend/.env</h3>
          <table className="w-full text-xs border border-slate-200 mt-2">
            <thead className="bg-slate-50 text-left"><tr><th className="p-2 border-b">Variable</th><th className="p-2 border-b">Used by</th><th className="p-2 border-b">Example</th></tr></thead>
            <tbody>
              <Row r={['REACT_APP_BACKEND_URL', 'api/client.js — every API call', 'https://esd.yourcompany.com (no trailing slash, no /api)']} />
              <Row r={['WDS_SOCKET_PORT', 'Webpack dev server (dev only)', '443']} />
              <Row r={['ENABLE_HEALTH_CHECK', 'Optional — toggles GET /api/ probe', 'true']} />
            </tbody>
          </table>

          <Note>
            <strong>Critical rule:</strong> the frontend NEVER hard‑codes URLs. All API calls go through <code className="bg-slate-100 px-1 rounded">{`${'$'}{REACT_APP_BACKEND_URL}/api/...`}</code>. Kubernetes/ingress is responsible for routing the <code className="bg-slate-100 px-1 rounded">/api/*</code> prefix to port 8001 and everything else to port 3000 (or your static build).
          </Note>
        </Section>

        {/* 4 — SUPERVISOR / PROCESS */}
        <Section title="4 · Process &amp; Service Topology" icon={Layers} pageBreak>
          <p className="text-sm">In the current container both processes are kept alive by supervisord. For your own cloud server, replicate either with <strong>systemd</strong>, <strong>Docker Compose</strong>, or a managed PaaS.</p>
          <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700 mt-3">Supervisor entry points</h3>
          <table className="w-full text-xs border border-slate-200 mt-2">
            <thead className="bg-slate-50 text-left"><tr><th className="p-2 border-b">Service</th><th className="p-2 border-b">Command</th><th className="p-2 border-b">Working dir</th></tr></thead>
            <tbody>
              <Row r={['backend', 'uvicorn server:app --host 0.0.0.0 --port 8001 --reload', '/app/backend']} mono />
              <Row r={['frontend', 'yarn start  (dev)  /  serve -s build -l 3000  (prod)', '/app/frontend']} mono />
            </tbody>
          </table>

          <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700 mt-4">Nginx (or ingress) routing rules</h3>
          <pre className="text-[11px] leading-relaxed bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto whitespace-pre">
{`server {
  listen 443 ssl;
  server_name esd.yourcompany.com;

  # Static frontend build (recommended in production)
  root /var/www/esd-frontend/build;
  index index.html;

  # All API calls forwarded to FastAPI
  location /api/ {
    proxy_pass         http://127.0.0.1:8001;
    proxy_set_header   Host $host;
    proxy_set_header   X-Real-IP $remote_addr;
    proxy_set_header   X-Forwarded-For $proxy_add_x_forwarded_for;
    proxy_set_header   X-Forwarded-Proto $scheme;
    client_max_body_size 50M;          # for inspection PDFs / signatures / Excel uploads
  }

  # SPA fallback so /track/:token, /guide, etc. work after refresh
  location / {
    try_files $uri /index.html;
  }
}`}
          </pre>
        </Section>

        {/* 5 — FRONTEND ROUTE MAP */}
        <Section title="5 · Frontend Routes &amp; Page Files" icon={Globe} pageBreak>
          <table className="w-full text-xs border border-slate-200">
            <thead className="bg-slate-50 text-left"><tr><th className="p-2 border-b">Path</th><th className="p-2 border-b">React component</th><th className="p-2 border-b">Auth</th><th className="p-2 border-b">Backend endpoints called</th></tr></thead>
            <tbody>
              {FRONTEND_ROUTES.map((r) => <Row key={r[0]} r={r} mono />)}
            </tbody>
          </table>
        </Section>

        {/* 6 — TRACE MATRIX (the big one) */}
        <Section title="6 · API → Code → MongoDB Trace" icon={Database} pageBreak>
          <p className="text-sm mb-3">For every endpoint: the FastAPI handler in <code className="bg-slate-100 px-1 rounded">server.py</code>, the Pydantic model in <code className="bg-slate-100 px-1 rounded">models.py</code>, and the Mongo collection it touches.</p>
          {API_GROUPS.map((grp) => (
            <div key={grp.title} className="mb-4">
              <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700 mt-3 mb-1">{grp.title}</h3>
              <table className="w-full text-[11px] border border-slate-200">
                <thead className="bg-slate-50 text-left">
                  <tr><th className="p-2 border-b w-8">M</th><th className="p-2 border-b">Endpoint</th><th className="p-2 border-b">Handler in server.py</th><th className="p-2 border-b">Model</th><th className="p-2 border-b">Collection</th></tr>
                </thead>
                <tbody>{grp.rows.map((r) => <Row key={`${grp.name || ''}-${r[0]}-${r[1] || ''}`} r={r} mono />)}</tbody>
              </table>
            </div>
          ))}
        </Section>

        {/* 7 — MONGO COLLECTIONS */}
        <Section title="7 · MongoDB Collections &amp; Key Fields" icon={Database} pageBreak>
          <p className="text-xs text-slate-500 mb-2">Document IDs are always stored under field <code className="bg-slate-100 px-1 rounded">id</code> (a UUID string) — never the Mongo native <code className="bg-slate-100 px-1 rounded">_id</code>. The <code className="bg-slate-100 px-1 rounded">_clean()</code> helper strips <code className="bg-slate-100 px-1 rounded">_id</code> from every response.</p>
          <table className="w-full text-[11px] border border-slate-200">
            <thead className="bg-slate-50 text-left"><tr><th className="p-2 border-b">Collection</th><th className="p-2 border-b">Key fields</th><th className="p-2 border-b">Used by modules</th></tr></thead>
            <tbody>{COLLECTIONS.map((c) => <Row key={c[0]} r={c} mono />)}</tbody>
          </table>
          <Note>
            <strong>Backup &amp; restore:</strong> <code className="bg-slate-100 px-1 rounded">POST /api/admin/backup</code> exports every collection above to a single JSON file; <code className="bg-slate-100 px-1 rounded">POST /api/admin/restore</code> reads it back. For server‑side migration prefer <code className="bg-slate-100 px-1 rounded">mongodump</code> + <code className="bg-slate-100 px-1 rounded">mongorestore</code>.
          </Note>
        </Section>

        {/* 8 — DEPENDENCIES */}
        <Section title="8 · Dependencies (pinned)" icon={Layers} pageBreak>
          <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700">Backend — /app/backend/requirements.txt</h3>
          <pre className="text-[11px] leading-relaxed bg-slate-50 text-slate-800 rounded-md p-3 overflow-x-auto whitespace-pre mt-1">
{`fastapi==0.110.1     pymongo==4.5.0       motor==3.3.1
uvicorn==0.25.0      pydantic>=2.6.4      pyjwt>=2.10.1
bcrypt==4.1.3        passlib>=1.7.4       python-jose>=3.3.0
python-dotenv>=1.0.1 python-multipart>=0.0.9
pandas>=2.2.0        numpy>=1.26.0  (Excel/CSV bulk upload)
pytest>=8.0.0`}
          </pre>

          <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700 mt-4">Frontend — /app/frontend/package.json (key deps)</h3>
          <pre className="text-[11px] leading-relaxed bg-slate-50 text-slate-800 rounded-md p-3 overflow-x-auto whitespace-pre mt-1">
{`react@19.x · react-router-dom · @craco/craco
tailwindcss · postcss · autoprefixer
@radix-ui/* (shadcn primitives) · lucide-react · sonner
axios · qrcode.react · signature_pad`}
          </pre>
        </Section>

        {/* 9 — AUTH / SECURITY */}
        <Section title="9 · Authentication &amp; Authorization" icon={ShieldCheck} pageBreak>
          <ul className="text-sm list-disc list-inside space-y-1">
            <li><strong>JWT</strong> issued by <code className="bg-slate-100 px-1 rounded">POST /api/auth/login</code>. Stored in <code className="bg-slate-100 px-1 rounded">localStorage</code> by <code className="bg-slate-100 px-1 rounded">AuthContext.jsx</code> and sent as <code className="bg-slate-100 px-1 rounded">Authorization: Bearer ...</code> via the Axios interceptor in <code className="bg-slate-100 px-1 rounded">api/client.js</code>.</li>
            <li><strong>Passwords</strong> hashed with bcrypt (<code className="bg-slate-100 px-1 rounded">auth_utils.py</code>).</li>
            <li><strong>Roles</strong>: <code>super_admin</code>, <code>admin</code>, <code>supervisor</code>, <code>approver</code>, <code>requestor</code>, <code>operator</code> — checked server‑side via <code className="bg-slate-100 px-1 rounded">require_roles(...)</code> dependency.</li>
            <li><strong>RBAC</strong> matrix (per-module permissions) stored in <code className="bg-slate-100 px-1 rounded">roles</code> collection; configurable via Settings → Roles &amp; Permissions. Modules include the standard CRUD set plus <code>equipment_location</code> (view/edit/approve) and <code>equipment_photos</code> (view/upload/delete/approve). The <code>super_admin</code> role is rendered with the full catalog regardless of stored perms.</li>
            <li><strong>Approval Workflow entities</strong> include <code>equipment_request</code>, <code>inspection</code>, <code>mobilization</code>, <code>maintenance</code>, <code>purchase_request</code>, plus <code>equipment_location_update</code> and <code>equipment_photo_upload</code>. When an entity has approval enabled, non-approver submissions arrive as <em>pending</em> and notify configured approvers.</li>
            <li><strong>Public surfaces (no auth)</strong>: <code>GET /api/</code> (health), <code>GET /api/public/mobilizations/{`{token}`}</code>, and the React route <code>/track/:token</code>.</li>
          </ul>
        </Section>

        {/* 10 — MIGRATION RUNBOOK */}
        <Section title="10 · Cloud Server Migration Runbook" icon={Server} pageBreak>
          <ol className="text-sm list-decimal list-inside space-y-2">
            <li><strong>Provision</strong> the host (Ubuntu 22.04 LTS recommended) with Python 3.11+, Node 18+, yarn, nginx, and access to a MongoDB instance (Atlas or self-hosted with auth).</li>
            <li><strong>Clone</strong> the repo: <code className="bg-slate-100 px-1 rounded">git clone &lt;your-repo&gt; /opt/esd</code></li>
            <li><strong>Backend</strong>: <code className="bg-slate-100 px-1 rounded">cd /opt/esd/backend &amp;&amp; python3 -m venv venv &amp;&amp; ./venv/bin/pip install -r requirements.txt</code></li>
            <li><strong>Backend .env</strong>: create <code className="bg-slate-100 px-1 rounded">/opt/esd/backend/.env</code> with the four variables in §3. Generate JWT_SECRET via <code className="bg-slate-100 px-1 rounded">{`python -c "import secrets;print(secrets.token_urlsafe(48))"`}</code>.</li>
            <li><strong>Frontend build</strong>: <code className="bg-slate-100 px-1 rounded">cd /opt/esd/frontend &amp;&amp; yarn install &amp;&amp; REACT_APP_BACKEND_URL=https://esd.yourcompany.com yarn build</code></li>
            <li><strong>Static files</strong>: copy <code className="bg-slate-100 px-1 rounded">/opt/esd/frontend/build/</code> to <code className="bg-slate-100 px-1 rounded">/var/www/esd-frontend/build</code></li>
            <li><strong>Systemd unit</strong> for backend (see snippet below). Enable and start.</li>
            <li><strong>Nginx</strong>: drop the config from §4 into <code className="bg-slate-100 px-1 rounded">/etc/nginx/sites-enabled/esd</code>. Issue a cert (<code className="bg-slate-100 px-1 rounded">certbot --nginx -d esd.yourcompany.com</code>). Reload nginx.</li>
            <li><strong>Seed data</strong>: hit <code className="bg-slate-100 px-1 rounded">GET /api/</code> (creates super.admin/admin123) and immediately reset that password via the UI.</li>
            <li><strong>Restore (optional)</strong>: Settings → Data Management → Upload your previous JSON backup, OR <code className="bg-slate-100 px-1 rounded">mongorestore</code> a database dump from the previous environment.</li>
            <li><strong>Verify</strong>: login, open <code className="bg-slate-100 px-1 rounded">/mobilizations</code>, scan a QR with your phone — should open a public <code className="bg-slate-100 px-1 rounded">/track/:token</code> page over HTTPS.</li>
          </ol>

          <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700 mt-4">Sample /etc/systemd/system/esd-backend.service</h3>
          <pre className="text-[11px] leading-relaxed bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto whitespace-pre">
{`[Unit]
Description=ESD FastAPI backend
After=network.target

[Service]
WorkingDirectory=/opt/esd/backend
EnvironmentFile=/opt/esd/backend/.env
ExecStart=/opt/esd/backend/venv/bin/uvicorn server:app --host 127.0.0.1 --port 8001 --workers 2
Restart=on-failure
RestartSec=3
User=www-data

[Install]
WantedBy=multi-user.target`}
          </pre>

          <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700 mt-4">Alternative: docker-compose.yml (sketch)</h3>
          <pre className="text-[11px] leading-relaxed bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto whitespace-pre">
{`services:
  mongo:
    image: mongo:6
    volumes: ["mongo_data:/data/db"]
  backend:
    build: ./backend
    env_file: ./backend/.env
    depends_on: [mongo]
    ports: ["8001:8001"]
  frontend:
    build: ./frontend
    environment:
      - REACT_APP_BACKEND_URL=https://esd.yourcompany.com
    ports: ["3000:3000"]
volumes:
  mongo_data: {}`}
          </pre>
        </Section>

        {/* 11 — OPS CHECKLIST */}
        <Section title="11 · Day‑2 Operations Checklist" icon={ShieldCheck}>
          <ul className="text-sm list-disc list-inside space-y-1">
            <li><strong>Backups</strong> — schedule a nightly <code className="bg-slate-100 px-1 rounded">mongodump</code> off-host. Also keep the in-app JSON backup as a quick‑restore option.</li>
            <li><strong>JWT_SECRET rotation</strong> — rotate every 90 days; all sessions invalidate, users re‑login.</li>
            <li><strong>Audit trail</strong> — the <code>audit_logs</code> collection grows forever. Cap it with a TTL index or rotate quarterly into cold storage.</li>
            <li><strong>Inspection PDFs</strong> — currently embedded base64 in the <code>inspections</code> documents. Consider moving to S3/GCS if volumes grow.</li>
            <li><strong>Signature images</strong> — stored as base64 PNG (≤80KB each) in the <code>mobilizations.signatures</code> sub-object. No external file system required.</li>
            <li><strong>Public tracking page</strong> — uses an opaque 24-char token. Rotate by clearing <code>public_token</code> and letting the system regenerate on next GET.</li>
            <li><strong>Roles &amp; Approval Workflow</strong> — re-verify after any new module is added; ensure default approvers exist.</li>
            <li><strong>Timestamps</strong> — backend persists naive UTC via <code className="bg-slate-100 px-1 rounded">_now_iso()</code> (no <code>Z</code> suffix). The frontend helper <code className="bg-slate-100 px-1 rounded">formatDateTime()</code> in <code>src/lib/format.js</code> appends <code>Z</code>, parses as UTC, and renders in <code>Asia/Manila</code> (GMT+8). To change deployment timezone, edit the <code>TZ</code> constant in that file or set the user&rsquo;s browser locale &mdash; no backend change required.</li>
            <li><strong>Photos</strong> — stored as base64 JPEG inside <code>vehicles.photos[]</code> (compressed client-side to ≤200 KB, capped at 12 per equipment). Move to object storage (S3/GCS) if you grow beyond ~5,000 active equipment with photos.</li>
          </ul>
        </Section>

        <footer className="px-10 py-4 border-t text-[10px] text-center text-slate-500 italic">
          {branding?.footer_disclaimer || 'This is a computer-generated document and does not require a signature for reference purposes.'}
          <div className="mt-1">ESD — Equipment Solutions Department · Technical Architecture &amp; Migration Handbook · {today}</div>
        </footer>
      </article>
    </div>
  );
};

const Section = ({ title, icon: Icon, pageBreak, children }) => (
  <section className={`px-10 py-7 border-t border-slate-200 ${pageBreak ? 'print:break-before-page' : ''}`}>
    <h2 className="flex items-center gap-2 text-lg font-bold tracking-tight text-[hsl(217,91%,24%)] mb-4">{Icon && <Icon className="h-5 w-5" />}{title}</h2>
    <div className="space-y-2 text-slate-800">{children}</div>
  </section>
);

const Row = ({ r, mono = false }) => {
  // Use row's first cell as a stable prefix so cell keys survive re-renders
  // even in the (rare) case rows themselves reorder.
  const rowId = r[0] ?? '';
  return (
    <tr className="border-b border-slate-100 align-top">
      {r.map((c, i) => <td key={`${rowId}-col-${i}`} className={`p-2 ${mono && (i === 1 || i === 0) ? 'font-mono' : ''}`}>{c}</td>)}
    </tr>
  );
};

const Note = ({ children }) => (
  <div className="mt-3 text-[11px] text-slate-700 bg-amber-50 border border-amber-200 rounded p-2.5">{children}</div>
);

const ArchSVG = () => (
  <svg viewBox="0 0 760 220" className="w-full mt-4" role="img" aria-label="Deployment topology">
    <defs>
      <marker id="td-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
        <path d="M 0 0 L 10 5 L 0 10 z" fill="#475569" />
      </marker>
    </defs>
    <rect x="20" y="40" width="140" height="60" rx="6" fill="#dbeafe" stroke="#1e40af" />
    <text x="90" y="65" textAnchor="middle" fontSize="12" fontWeight="700" fill="#1e3a8a">Browser / Mobile</text>
    <text x="90" y="82" textAnchor="middle" fontSize="10" fill="#1e3a8a" opacity="0.75">React SPA · QR scan</text>
    <rect x="220" y="40" width="160" height="60" rx="6" fill="#e0e7ff" stroke="#4338ca" />
    <text x="300" y="65" textAnchor="middle" fontSize="12" fontWeight="700" fill="#3730a3">nginx / ingress</text>
    <text x="300" y="82" textAnchor="middle" fontSize="10" fill="#3730a3" opacity="0.75">routes /api → :8001</text>
    <rect x="440" y="20" width="140" height="60" rx="6" fill="#d1fae5" stroke="#047857" />
    <text x="510" y="45" textAnchor="middle" fontSize="12" fontWeight="700" fill="#065f46">FastAPI :8001</text>
    <text x="510" y="62" textAnchor="middle" fontSize="10" fill="#065f46" opacity="0.75">server.py · uvicorn</text>
    <rect x="440" y="110" width="140" height="60" rx="6" fill="#fef3c7" stroke="#b45309" />
    <text x="510" y="135" textAnchor="middle" fontSize="12" fontWeight="700" fill="#92400e">Static build :3000</text>
    <text x="510" y="152" textAnchor="middle" fontSize="10" fill="#92400e" opacity="0.75">/var/www/.../build</text>
    <rect x="630" y="65" width="110" height="60" rx="6" fill="#e2e8f0" stroke="#475569" />
    <text x="685" y="90" textAnchor="middle" fontSize="12" fontWeight="700" fill="#1e293b">MongoDB</text>
    <text x="685" y="107" textAnchor="middle" fontSize="10" fill="#1e293b" opacity="0.75">collections (see §7)</text>
    <line x1="160" y1="70" x2="220" y2="70" stroke="#475569" strokeWidth="1.4" markerEnd="url(#td-arr)" />
    <line x1="380" y1="55" x2="440" y2="50" stroke="#475569" strokeWidth="1.4" markerEnd="url(#td-arr)" />
    <line x1="380" y1="85" x2="440" y2="135" stroke="#475569" strokeWidth="1.4" markerEnd="url(#td-arr)" />
    <line x1="580" y1="50" x2="630" y2="85" stroke="#475569" strokeWidth="1.4" markerEnd="url(#td-arr)" />
    <text x="600" y="190" textAnchor="middle" fontSize="9" fill="#475569" fontStyle="italic">backend → DB on internal network only</text>
  </svg>
);

/* ---------------- Static reference data ---------------- */
const FRONTEND_ROUTES = [
  ['/login', 'pages/Login.jsx', 'public', 'POST /auth/login'],
  ['/track/:token', 'pages/PublicTracking.jsx', 'PUBLIC (no auth)', 'GET /public/mobilizations/{token}'],
  ['/dashboard', 'pages/Dashboard.jsx', 'auth', 'GET /vehicles, /operators, /equipment-requests, /inspections, /mobilizations, /maintenance'],
  ['/equipment', 'pages/Equipment.jsx', 'auth', 'GET/POST/PUT/PATCH/DELETE /vehicles, /vehicles-template, /vehicles-upload'],
  ['/operators', 'pages/Operators.jsx', 'sup+', 'GET/POST/PUT/DELETE /operators'],
  ['/equipment-requests', 'pages/EquipmentRequests.jsx', 'auth', 'GET/POST /equipment-requests, /approve, /reject'],
  ['/inspections', 'pages/Inspections.jsx', 'sup+', 'GET/PUT /inspections, /approve, /reject'],
  ['/equipment-queues', 'pages/EquipmentQueues.jsx', 'sup+', '(uses /inspections + /vehicles)'],
  ['/mobilizations', 'pages/Mobilizations.jsx', 'auth', 'GET/PUT/DELETE /mobilizations, /available-operators'],
  ['/mobilizations/:id/form', 'pages/MobilizationForm.jsx', 'auth', 'GET/PUT /mobilizations/{id}, /form, /signatures'],
  ['/maintenance', 'pages/Maintenance.jsx', 'sup+', 'GET/POST/PUT/DELETE /maintenance'],
  ['/consumables', 'pages/Consumables.jsx', 'auth', 'GET/POST/PUT/DELETE /consumables, /consumables/summary, /purchase-requests, /transition'],
  ['/utilizations', 'pages/Utilizations.jsx', 'auth', 'GET/POST/PUT/DELETE /utilizations, /utilizations/summary'],
  ['/contracts', 'pages/Contracts.jsx', 'admin', 'GET/POST/PUT/DELETE /contracts'],
  ['/billing', 'pages/Billing.jsx', 'admin', 'GET/POST/PUT/DELETE /billings'],
  ['/settings/*', 'pages/Settings.jsx + sub-tabs', 'admin', 'GET/PUT /users, /roles, /approval-workflow, /dropdowns, /settings/branding, /admin/backup|restore|purge'],
  ['/guide', 'pages/SystemGuide.jsx', 'auth', 'GET /settings/branding'],
  ['/docs/architecture', 'pages/TechnicalDoc.jsx', 'admin', 'GET /settings/branding'],
];

const API_GROUPS = [
  {
    title: 'Auth & Users',
    rows: [
      ['POST', '/auth/login', 'login()', 'LoginBody', 'users'],
      ['GET', '/auth/me', 'me()', '—', 'users'],
      ['GET/POST/PUT/DELETE', '/users · /users/{id}', 'list/create/update/delete_user()', 'UserCreate/Update', 'users'],
      ['POST', '/users/{id}/reset-password', 'reset_password()', 'PasswordReset', 'users'],
    ],
  },
  {
    title: 'Equipment & Operators',
    rows: [
      ['GET/POST/PUT/PATCH/DELETE', '/vehicles · /vehicles/{id}', 'list/create/update/patch/delete_vehicle()', 'VehicleCreate/Update', 'vehicles'],
      ['GET', '/vehicles-template · /vehicles/available-for-operator', 'vehicles_template / available_for_operator()', '—', 'vehicles'],
      ['POST', '/vehicles-upload · /vehicles/batch-delete', 'upload_vehicles_excel / batch_delete()', 'multipart / list[id]', 'vehicles'],
      ['PUT', '/vehicles/{id}/location · /location/approve|reject', 'update / approve / reject_location()', 'VehicleLocationUpdate', 'vehicles + audit_logs'],
      ['GET', '/vehicles/{id}/location-history', 'list_location_history()', '—', 'vehicles.location_history[]'],
      ['GET/POST', '/vehicles/{id}/photos', 'list / upload_photo()', 'multipart (image_base64 + caption)', 'vehicles.photos[]'],
      ['POST', '/vehicles/{id}/photos/{pid}/approve|reject', 'approve / reject_photo()', '—', 'vehicles.photos[].status'],
      ['DELETE', '/vehicles/{id}/photos/{pid}', 'delete_photo()', '—', 'vehicles.photos[] (uploader or admin)'],
      ['GET/POST/PUT/DELETE', '/operators · /operators/{id}', 'list/create/update/delete_operator()', 'OperatorCreate/Update', 'operators'],
    ],
  },
  {
    title: 'Requests · Inspections · Mobilizations',
    rows: [
      ['GET/POST', '/equipment-requests', 'list/create_request()', 'EquipmentRequestCreate', 'equipment_requests'],
      ['POST', '/equipment-requests/{id}/approve|reject', 'approve/reject_request()', 'ApproveBody', 'equipment_requests + inspections (auto)'],
      ['GET/PUT', '/inspections · /inspections/{id}', 'list/get/update_inspection()', 'InspectionUpdate', 'inspections'],
      ['POST', '/inspections/{id}/approve|reject', 'approve/reject_inspection()', 'RejectBody', 'inspections + mobilizations (auto-creates)'],
      ['GET/PUT/DELETE', '/mobilizations · /mobilizations/{id}', 'list/update/delete_mobilization()', 'MobilizationUpdate', 'mobilizations'],
      ['PUT', '/mobilizations/{id}/form', 'update_mobilization_form()', 'MobilizationFormUpdate', 'mobilizations'],
      ['PUT', '/mobilizations/{id}/signatures', 'save_signature()', 'MobilizationSignature', 'mobilizations.signatures'],
      ['POST', '/mobilizations/{id}/signatures/{role}/clear', 'clear_signature()', '—', 'mobilizations.signatures'],
      ['GET', '/public/mobilizations/{token}  ← NO AUTH', 'public_track()', '—', 'mobilizations + vehicles + operators'],
    ],
  },
  {
    title: 'Maintenance · Utilizations · Consumables · Purchase Requests',
    rows: [
      ['GET/POST/PUT/DELETE', '/maintenance · /maintenance/{id}', 'list/create/update/delete_maintenance()', 'MaintenanceCreate/Update', 'maintenance'],
      ['GET/POST/PUT/DELETE', '/utilizations · /utilizations/{id}', 'list/create/update/delete_utilization()', 'UtilizationCreate/Update', 'utilizations'],
      ['GET', '/utilizations/summary', 'utilizations_summary()', '—', 'utilizations (read-only agg)'],
      ['GET/POST/PUT/DELETE', '/consumables · /consumables/{id}', 'list/create/update/delete_consumable_log()', 'ConsumableLogCreate/Update', 'consumable_logs'],
      ['GET', '/consumables/summary', 'consumables_summary()', '—', 'consumable_logs (read-only agg)'],
      ['GET/POST/PUT/DELETE', '/purchase-requests · /purchase-requests/{id}', 'list/create/update/delete_purchase_request()', 'PurchaseRequestCreate/Update', 'purchase_requests'],
      ['POST', '/purchase-requests/{id}/transition', 'transition_purchase_request()', 'PurchaseStatusTransition', 'purchase_requests + consumable_logs (on Received)'],
    ],
  },
  {
    title: 'Contracts · Billings · Notifications · Audit',
    rows: [
      ['GET/POST/PUT/DELETE', '/contracts · /contracts/{id}', 'list/create/update/delete_contract()', 'ContractCreate/Update', 'contracts'],
      ['GET/POST/PUT/DELETE', '/billings · /billings/{id}', 'list/create/update/delete_billing()', 'BillingCreate/Update', 'billings'],
      ['GET · POST .../read · POST /read-all', '/notifications', 'list/mark_*_notifications()', '—', 'notifications'],
      ['GET', '/audit-logs', 'list_audit_logs()', '—', 'audit_logs (read-only)'],
    ],
  },
  {
    title: 'Settings · Dropdowns · Roles · Branding · Admin',
    rows: [
      ['GET/POST/PUT/DELETE', '/dropdowns · /dropdowns/{id}', 'list/create/update/delete_dropdown()', 'DropdownOptionCreate/Update', 'dropdown_options'],
      ['POST', '/dropdowns/sync-from-data', 'sync_dropdowns_from_data()', '—', 'dropdown_options (upsert)'],
      ['GET/PUT', '/settings/branding', 'get/update_branding()', 'BrandingSettings', 'app_settings (id="branding")'],
      ['GET/POST/PUT/DELETE', '/roles · /permissions/catalog', 'list/create/update/delete_role + perm catalog', 'RoleCreate/Update', 'roles'],
      ['GET/PUT', '/approval-workflow · /approval-workflow/entities', 'list/upsert_workflow + entities catalog', '—', 'approval_assignments'],
      ['POST', '/admin/backup · /admin/restore · /admin/purge', 'backup / restore / purge()', '—', 'every collection in §7'],
    ],
  },
];

const COLLECTIONS = [
  ['users', 'id, username, full_name, password_hash (bcrypt), role, is_active', 'Auth, RBAC'],
  ['roles', 'key, name, description, permissions[], is_builtin', 'RBAC matrix'],
  ['approval_assignments', 'entity_type, approver_role_keys[], approver_user_ids[], enabled', 'Approval workflow'],
  ['vehicles', 'id, equipment_code, plate_no, category, description, brand, model, status, area, project_location, location{lat,lng,label,source,submitted_at}, pending_location, location_history[], photos[{id, image_base64, caption, status, uploaded_by, uploaded_at, size_kb}]', 'Equipment, Location & Photos, Mobilization, Utilization, Maintenance'],
  ['operators', 'id, full_name, license_no, nc_ii_certificates[], assigned_vehicle_id, status, is_available', 'Operators, Mobilization'],
  ['equipment_requests', 'id, reference_number, requestor_id, requested_category, status, vehicle_id', 'Requests'],
  ['inspections', 'id, request_id, vehicle_id, operator_id, status, pdf_report_data, is_queued', 'Inspections, Equipment Queues'],
  ['mobilizations', 'id, reference_number, serial_number, public_token, vehicle_id, operator_id, status, project_*, signatures{coordinator,operator,receiver}, status_history[]', 'Mobilizations, Public tracking'],
  ['maintenance', 'id, vehicle_id, description, type, status, scheduled_date, completed_date, cost', 'Maintenance'],
  ['utilizations', 'id, reference_number, vehicle_id, date, shift, hours_used, idle_hours, productive_hours, fuel_consumed, odometer_start/end, kilometers, operator_id', 'Utilizations'],
  ['consumable_logs', 'id, reference_number, log_date, vehicle_id, work_order_id, item_name, item_type, brand, part_number, unit, quantity, unit_price, total_cost, reason, technician, purchase_request_id', 'Consumables'],
  ['purchase_requests', 'id, reference_number, item_name, quantity, estimated_unit_price, estimated_total, priority, status, status_history[], assigned_to_user_id, vehicle_id, work_order_id, consumable_log_id', 'Purchase Requests'],
  ['contracts', 'id, contract_no, client_name, project_name, start_date, end_date, total_value, status', 'Contracts'],
  ['billings', 'id, invoice_no, contract_id, amount, status, issue_date, due_date', 'Billing'],
  ['notifications', 'id, user_id, title, message, level, priority, link, read_at', 'In-app notifications'],
  ['audit_logs', 'id, action, user_id, entity, entity_id, details, ts', 'Audit trail (immutable)'],
  ['dropdown_options', 'id, category, value', 'Centralized dropdowns'],
  ['app_settings', 'id (e.g. "branding"), header_text, subtitle, logo_url, footer_disclaimer, tracking_base_url', 'Form branding'],
  ['equipment_queues', 'id, inspection_id, vehicle_id, category, status, alternative_vehicle_id', 'Equipment Queues'],
  ['counters', 'id (e.g. "request", "mobilization"), seq', 'Reference-number generator (CON/UTL/MOB/REQ/PR-…)'],
  ['backups', 'id, filename, size, records_count, created_at', 'Backup metadata'],
];

export default TechnicalDoc;
